import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Float, Text } from "@react-three/drei";
import { ArenaCanvas } from "../three/Arena";
import Avatar from "../three/Avatar";
import Orb from "../three/Orb";
import { FONTS, TEAM } from "../game/theme";
import { unlockAudio } from "../game/sounds";

// Floating neon question marks drifting around the stage.
function QuestionMarks() {
  const marks = useMemo(
    () =>
      Array.from({ length: 10 }, (_, i) => ({
        pos: [
          Math.sin(i * 2.1) * (6 + (i % 3) * 2),
          1.2 + (i % 5) * 1.3,
          -4 - (i % 4) * 1.5,
        ],
        size: 0.45 + (i % 3) * 0.35,
        color: i % 2 === 0 ? TEAM.left.color : TEAM.right.color,
        speed: 1 + (i % 3) * 0.6,
      })),
    []
  );
  return (
    <>
      {marks.map((m, i) => (
        <Float key={i} speed={m.speed} rotationIntensity={0.7} floatIntensity={1.6}>
          <Text
            font={FONTS.display}
            position={m.pos}
            fontSize={m.size}
            color={m.color}
            outlineWidth={0.02}
            outlineColor="#000000"
            fillOpacity={0.8}
          >
            ?
          </Text>
        </Float>
      ))}
    </>
  );
}

// Every few seconds one of the demo avatars "answers" — an orb arcs across.
function AmbientDuel() {
  const [orbs, setOrbs] = useState([]);
  const seq = useRef(0);
  const side = useRef(1);

  useEffect(() => {
    const t = setInterval(() => {
      side.current *= -1;
      const s = side.current;
      const id = ++seq.current;
      setOrbs((prev) => [
        ...prev.slice(-3),
        {
          id,
          from: [s * 3.4, 1.9, 0.5],
          to: [-s * 1.2, 4.5 + Math.random() * 1.5, -4],
          color: s < 0 ? TEAM.left.glow : TEAM.right.glow,
        },
      ]);
    }, 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      {orbs.map((o) => (
        <Orb
          key={o.id}
          from={o.from}
          to={o.to}
          color={o.color}
          duration={1.1}
          onArrive={() => setOrbs((prev) => prev.filter((x) => x.id !== o.id))}
        />
      ))}
    </>
  );
}

const demoLeft = { id: "demo-lefty", name: "Titan", team: "left" };
const demoRight = { id: "demo-viper", name: "Viper", team: "right" };

const LandingPage = () => {
  return (
    <>
      <ArenaCanvas cameraBase={[0, 2.6, 9.5]} cameraLook={[0, 1.8, -1]} sway={1.8}>
        <QuestionMarks />
        <AmbientDuel />
        <Avatar player={demoLeft} position={[-3.4, 0, 0.5]} />
        <Avatar player={demoRight} position={[3.4, 0, 0.5]} />
      </ArenaCanvas>

      <div className="hero" onPointerDown={unlockAudio}>
        <h1 className="hero-title">
          <span className="l1">GUESS THEIR</span>
          <span className="l2">ANSWERS!</span>
        </h1>
        <p className="hero-tag">
          THE 3D PARTY GAME SHOW —{" "}
          <span className="vs-lime">GREEN TITANS</span> VS{" "}
          <span className="vs-violet">VIOLET VIPERS</span>
        </p>

        <div className="hero-actions">
          <Link to="/create-room" className="abtn abtn-lime abtn-big">
            HOST A SHOW
          </Link>
          <Link to="/join-room" className="abtn abtn-violet abtn-big">
            JOIN THE SQUAD
          </Link>
        </div>

        <div className="hero-steps">
          <div className="step-chip"><b>1</b> HOST PICKS A TOPIC</div>
          <div className="step-chip"><b>2</b> SQUAD JOINS WITH A CODE</div>
          <div className="step-chip"><b>3</b> FASTEST GUESS WINS THE BOARD</div>
        </div>
      </div>
    </>
  );
};

export default LandingPage;
